import { useState, useRef, useCallback } from 'react';
import type { WorkerRequest, WorkerResponse, OverlayParams } from './types';

export function isWebCodecsSupported(): boolean {
  return (
    typeof VideoEncoder !== 'undefined' &&
    typeof VideoDecoder !== 'undefined'
  );
}

export interface ProcessOptions {
  videoFile: File;
  overlay: OverlayParams | null;
  onProgress?: (pct: number) => void;
}

export function useWebCodecsProcessor() {
  const [isProcessing, setIsProcessing] = useState(false);
  const workerRef = useRef<Worker | null>(null);
  const abortRef = useRef<(() => void) | null>(null);

  const process = useCallback(
    ({ videoFile, overlay, onProgress }: ProcessOptions): Promise<Blob> => {
      return new Promise((resolve, reject) => {
        const worker = new Worker(
          new URL('./webcodecs-worker.ts', import.meta.url),
          { type: 'module' },
        );
        workerRef.current = worker;
        let cancelled = false;

        abortRef.current = () => {
          cancelled = true;
          worker.terminate();
          workerRef.current = null;
          reject(new Error('Processing cancelled'));
        };

        worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
          if (cancelled) return;
          const msg = e.data;
          if (msg.type === 'progress') {
            onProgress?.(msg.percent);
          } else if (msg.type === 'complete') {
            workerRef.current = null;
            setIsProcessing(false);
            resolve(new Blob([msg.resultBuffer], { type: 'video/mp4' }));
          } else if (msg.type === 'error') {
            workerRef.current = null;
            setIsProcessing(false);
            const err = new Error(msg.message);
            if (msg.stack) err.stack = msg.stack;
            reject(err);
          }
        };

        worker.onerror = (e) => {
          if (cancelled) return;
          workerRef.current = null;
          setIsProcessing(false);
          reject(new Error(e.message));
        };

        setIsProcessing(true);

        videoFile.arrayBuffer().then((videoArrayBuffer) => {
          if (cancelled) return;
          const request: WorkerRequest = { type: 'process', videoArrayBuffer, overlay };
          const transferables: Transferable[] = [videoArrayBuffer];
          if (overlay?.logoArrayBuffer) transferables.push(overlay.logoArrayBuffer);
          if (overlay?.frameArrayBuffer) transferables.push(overlay.frameArrayBuffer);
          if (overlay?.reelBackgroundArrayBuffer) transferables.push(overlay.reelBackgroundArrayBuffer);
          worker.postMessage(request, transferables);
        }).catch((err) => {
          setIsProcessing(false);
          reject(err);
        });
      });
    },
    [],
  );

  const cancel = useCallback(() => {
    abortRef.current?.();
    abortRef.current = null;
    setIsProcessing(false);
  }, []);

  return {
    process,
    isSupported: isWebCodecsSupported(),
    isProcessing,
    cancel,
  };
}
